<template>
  <SecondaryNav>
    <template #top>
      <SecondaryTop :text="discount_code_name || 'New discount code'" />
    </template>
    <template #main>
      <div class="flex flex-col justify-between h-full">
        <form
          class="flex flex-1 flex-col justify-between"
          @submit.prevent="handleSubmit()"
          @keydown.enter="handleKeydown($event)"
          @keydown.meta.enter.exact="handleSubmit()"
        >
          <PageGrid>
            <MainCol>
              <BasicContainer>
                <div class="grid grid-cols-12 gap-6">
                  <div class="col-span-12">
                    <label for="name" class="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      id="name"
                      v-model="discount_code_name"
                      type="text"
                      name="name"
                      class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                  <div class="col-span-12">
                    <label for="code" class="block text-sm font-medium text-gray-700 mb-1">Code</label>
                    <input
                      id="code"
                      v-model="code"
                      type="text"
                      name="code"
                      class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
                    />
                    <p v-if="codeError" class="mt-2 text-sm text-red-600">{{ codeError }}</p>
                  </div>
                  <div v-if="type === 'PercentOffDiscountCode'" class="col-span-12">
                    <InputField
                      v-model="discount_rate"
                      name="Discount Amount"
                      prefix="%"
                      restrict="float"
                      :pattern="decimalNumberPattern"
                      title="Numbers only"
                    />
                  </div>
                  <div v-if="type === 'PercentOffDiscountCode'" class="col-span-12">
                    <label for="max_discount_amount" class="block text-sm font-medium text-gray-700 mb-1">
                      Maximum discount amount
                      <!-- ({{ discountCode.maximum_discount_amount.cents }}) -->
                    </label>

                    <input
                      id="max_discount_amount"
                      v-model="maximum_discount_amount.dollars"
                      type="text"
                      inputmode="numeric"
                      pattern="^(([0-9]*)|(([0-9]*)\.([0-9]*)))$"
                      title="Numbers only"
                      name="max_discount_amount"
                      class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                  <div v-if="type === 'AmountOffDiscountCode'" class="col-span-12">
                    <InputField
                      v-model="discount_amount.dollars"
                      name="Discount Amount"
                      prefix="$"
                      restrict="monetary"
                      :pattern="decimalNumberPattern"
                      title="Numbers only"
                    />
                  </div>
                  <div class="col-span-12">
                    <label for="max_uses" class="block text-sm font-medium text-gray-700 mb-1">
                      Max use times per user
                    </label>
                    <input
                      id="max_uses"
                      v-model="max_use_times_per_user"
                      type="text"
                      inputmode="numeric"
                      pattern="^(0|[1-9][0-9]*)$"
                      title="Numbers only"
                      name="max_uses"
                      class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
                    />
                  </div>
                </div>
              </BasicContainer>
              <BasicContainer>
                <h3 class="text-lg leading-6 font-medium text-gray-900">Options</h3>
                <div class="grid grid-cols-12 gap-6 border-b border-gray-300 pb-6">
                  <div class="col-span-6">
                    <label for="can_be_combined" class="block text-sm font-medium text-gray-700 mb-1">
                      Can be combined
                    </label>
                    <p class="mt-1 mb-2 text-xs text-gray-500">
                      Allow this code to be used together with other discount codes on the same order.
                    </p>

                    <button
                      id="can_be_combined"
                      type="button"
                      class="relative inline-flex flex-shrink-0 w-11 h-6 border-2 border-transparent rounded-full focus:outline-none cursor-pointer transition-colors duration-200 ease-in-out focus:ring-blue-500 focus:ring-offset-2 focus:ring-2"
                      :class="[can_be_combined ? 'bg-blue-600' : 'bg-gray-200']"
                      aria-pressed="false"
                      @click="can_be_combined = !can_be_combined"
                    >
                      <span
                        aria-hidden="true"
                        class="inline-block w-5 h-5 bg-white rounded-full shadow pointer-events-none transform transition duration-200 ease-in-out ring-0"
                        :class="[can_be_combined ? 'translate-x-5' : 'translate-x-0']"
                      ></span>
                    </button>
                  </div>
                  <div v-if="can_be_combined" class="col-span-6">
                    <label for="can_be_combined_always" class="block text-sm font-medium text-gray-700 mb-1">
                      Can be combined always
                    </label>
                    <p class="mt-1 mb-2 text-xs text-gray-500">
                      Always let this code stack with others, even codes marked as not combinable.
                    </p>
                    <button
                      id="can_be_combined_always"
                      type="button"
                      class="relative inline-flex flex-shrink-0 w-11 h-6 border-2 border-transparent rounded-full focus:outline-none cursor-pointer transition-colors duration-200 ease-in-out focus:ring-blue-500 focus:ring-offset-2 focus:ring-2"
                      :class="[can_be_combined_always ? 'bg-blue-600' : 'bg-gray-200']"
                      aria-pressed="false"
                      @click="can_be_combined_always = !can_be_combined_always"
                    >
                      <span
                        aria-hidden="true"
                        class="inline-block w-5 h-5 bg-white rounded-full shadow pointer-events-none transform transition duration-200 ease-in-out ring-0"
                        :class="[can_be_combined_always ? 'translate-x-5' : 'translate-x-0']"
                      ></span>
                    </button>
                  </div>
                </div>
                <div class="grid grid-cols-12 gap-6 border-b border-gray-300 pb-6">
                  <div class="col-span-6">
                    <label for="can_expire" class="block text-sm font-medium text-gray-700 mb-1">Can expire</label>
                    <p class="mt-1 mb-2 text-xs text-gray-500">
                      Give this code an expiry date, after which it can no longer be used.
                    </p>
                    <button
                      id="can_expire"
                      type="button"
                      class="relative inline-flex flex-shrink-0 w-11 h-6 border-2 border-transparent rounded-full focus:outline-none cursor-pointer transition-colors duration-200 ease-in-out focus:ring-blue-500 focus:ring-offset-2 focus:ring-2"
                      :class="[code_can_expire ? 'bg-blue-600' : 'bg-gray-200']"
                      aria-pressed="false"
                      @click="code_can_expire = !code_can_expire"
                    >
                      <span
                        aria-hidden="true"
                        class="inline-block w-5 h-5 bg-white rounded-full shadow pointer-events-none transform transition duration-200 ease-in-out ring-0"
                        :class="[code_can_expire ? 'translate-x-5' : 'translate-x-0']"
                      ></span>
                    </button>
                  </div>

                  <div v-if="code_can_expire" class="col-span-6">
                    <label for="expiration_date" class="block text-gray-700 text-sm font-medium sm:mt-px sm:pt-2">
                      Expiration date
                    </label>
                    <input
                      id="expiration_date"
                      v-model="expiration_date"
                      type="date"
                      name="expiration_date"
                      class="block w-full max-w-lg border-gray-300 focus:border-blue-500 rounded-md shadow-sm focus:ring-blue-500 sm:max-w-xs sm:text-sm"
                    />
                  </div>
                </div>
                <div class="grid grid-cols-12 gap-6 border-b border-gray-300 pb-6">
                  <div class="col-span-6">
                    <label for="first_time" class="block text-sm font-medium text-gray-700 mb-1">
                      First time customer only
                    </label>
                    <p class="mt-1 mb-2 text-xs text-gray-500">
                      Only customers who have never ordered before can use this code.
                    </p>

                    <button
                      id="first_time"
                      type="button"
                      class="relative inline-flex flex-shrink-0 w-11 h-6 border-2 border-transparent rounded-full focus:outline-none cursor-pointer transition-colors duration-200 ease-in-out focus:ring-blue-500 focus:ring-offset-2 focus:ring-2"
                      :class="[first_time_customer_only ? 'bg-blue-600' : 'bg-gray-200']"
                      aria-pressed="false"
                      @click="first_time_customer_only = !first_time_customer_only"
                    >
                      <span
                        aria-hidden="true"
                        class="inline-block w-5 h-5 bg-white rounded-full shadow pointer-events-none transform transition duration-200 ease-in-out ring-0"
                        :class="[first_time_customer_only ? 'translate-x-5' : 'translate-x-0']"
                      ></span>
                    </button>
                  </div>
                </div>
                <div class="grid grid-cols-12 gap-6 border-b border-gray-300 pb-6">
                  <div class="col-span-6">
                    <label for="auto_apply_once_used" class="block text-sm font-medium text-gray-700 mb-1">
                      Keep applying to customer once they use
                    </label>
                    <p class="mt-1 mb-2 text-xs text-gray-500">
                      After a customer uses this code once, reapply it automatically to their future orders.
                    </p>

                    <button
                      id="auto_apply_once_used"
                      type="button"
                      class="relative inline-flex flex-shrink-0 w-11 h-6 border-2 border-transparent rounded-full focus:outline-none cursor-pointer transition-colors duration-200 ease-in-out focus:ring-blue-500 focus:ring-offset-2 focus:ring-2"
                      :class="[auto_apply_once_used ? 'bg-blue-600' : 'bg-gray-200']"
                      aria-pressed="false"
                      @click="auto_apply_once_used = !auto_apply_once_used"
                    >
                      <span
                        aria-hidden="true"
                        class="inline-block w-5 h-5 bg-white rounded-full shadow pointer-events-none transform transition duration-200 ease-in-out ring-0"
                        :class="[auto_apply_once_used ? 'translate-x-5' : 'translate-x-0']"
                      ></span>
                    </button>
                  </div>
                </div>
                <div class="grid grid-cols-12 gap-6">
                  <div class="col-span-6">
                    <label for="automatically_apply" class="block text-sm font-medium text-gray-700 mb-1">
                      Automatically apply
                    </label>
                    <p class="mt-1 mb-2 text-xs text-gray-500">
                      Apply this code automatically at checkout for any customer whose order meets all its conditions,
                      without them entering it.
                    </p>

                    <button
                      id="automatically_apply"
                      type="button"
                      class="relative inline-flex flex-shrink-0 w-11 h-6 border-2 border-transparent rounded-full focus:outline-none cursor-pointer transition-colors duration-200 ease-in-out focus:ring-blue-500 focus:ring-offset-2 focus:ring-2"
                      :class="[automatically_apply ? 'bg-blue-600' : 'bg-gray-200']"
                      aria-pressed="false"
                      @click="automatically_apply = !automatically_apply"
                    >
                      <span
                        aria-hidden="true"
                        class="inline-block w-5 h-5 bg-white rounded-full shadow pointer-events-none transform transition duration-200 ease-in-out ring-0"
                        :class="[automatically_apply ? 'translate-x-5' : 'translate-x-0']"
                      ></span>
                    </button>
                  </div>
                </div>
              </BasicContainer>
            </MainCol>
            <SideCol>
              <BasicContainer>
                <div>
                  <!-- TODO:@llb replace with dropdown -->
                  <label for="type" class="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <div class="space-y-2">
                    <div class="flex items-center">
                      <input
                        id="percent_off"
                        v-model="type"
                        name="type"
                        type="radio"
                        value="PercentOffDiscountCode"
                        class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <label for="percent_off" class="ml-2 block text-sm font-medium text-gray-700 mb-1">
                        Percent off
                      </label>
                    </div>
                    <div class="flex items-center">
                      <input
                        id="amount_off"
                        v-model="type"
                        name="type"
                        value="AmountOffDiscountCode"
                        type="radio"
                        class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <label for="amount_off" class="ml-2 block text-sm font-medium text-gray-700 mb-1">
                        Amount off
                      </label>
                    </div>
                    <div class="flex items-center">
                      <input
                        id="free_shipping"
                        v-model="type"
                        name="type"
                        type="radio"
                        value="FreeShippingDiscountCode"
                        class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <label for="free_shipping" class="ml-2 block text-sm font-medium text-gray-700 mb-1">
                        Free shipping
                      </label>
                    </div>
                  </div>
                </div>
              </BasicContainer>
            </SideCol>
          </PageGrid>
          <PageFooter>
            <template #left>
              <button class="btn-ghost mr-3" @click="$router.back">Cancel</button>
            </template>
            <template #right>
              <button class="btn-primary" type="submit">Save</button>
            </template>
          </PageFooter>
        </form>
      </div>
    </template>
  </SecondaryNav>
</template>
<script>
import InputField from '@/components/neue/InputField'
import { DECIMAL_NUMBER_PATTERN } from '@/helpers/discountFormat'
import api from '@/store/api'

export default {
  components: { InputField },
  setup() {
    return { decimalNumberPattern: DECIMAL_NUMBER_PATTERN }
  },
  data() {
    return {
      type: '',
      discount_code_name: '',
      code: '',
      codeError: '',
      discount_rate: '',
      maximum_discount_amount: { dollars: '' },
      discount_amount: { dollars: '' },
      max_use_times_per_user: '',
      can_be_combined: false,
      can_be_combined_always: false,
      code_can_expire: false,
      expiration_date: '',
      first_time_customer_only: false,
      auto_apply_once_used: false,
      automatically_apply: false
    }
  },
  watch: {
    code() {
      const regexPattern = new RegExp('[^A-Za-z0-9_-]*$', 'g')
      this.code = this.code.toUpperCase().replace(/\s/g, '-').replace(regexPattern, '')
      this.codeError = ''
    }
  },
  methods: {
    handleKeydown(event) {
      // 'Enter' outside textarea
      if (event.keyCode === 13 && event.target.localName !== 'textarea') event.preventDefault()
    },
    handleSubmit() {
      this.codeError = ''

      const sendData = { ...this.$data }

      sendData.discount_rate = this.discount_rate / 100
      sendData.discount_amount_cents = sendData.discount_amount.dollars * 100
      delete sendData.discount_amount
      sendData.maximum_discount_amount_cents = sendData.maximum_discount_amount.dollars * 100
      delete sendData.maximum_discount_amount

      api
        .call('POST', `discount_codes`, {
          discount_code: sendData
        })
        .then(response => {
          this.$router.replace('/store/discount-codes/' + response.data.data.id)
        })
        .catch(error => {
          if (error.response?.status === 422) {
            const message = error.response.data?.errors?.[0]
            this.codeError = message || 'This code is already taken.'
            return
          }
          this.codeError = 'Unable to save right now. Please try again.'
        })
    }
  }
}
</script>
